import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { videoMap } from "../data/videoMap";
import { productById } from "../data/products";

export const VideoModal = ({ openId, onClose }) => {
  const { t } = useLanguage();

  React.useEffect(() => {
    if (openId) {
      const onKey = (e) => e.key === "Escape" && onClose();
      window.addEventListener("keydown", onKey);
      document.body.style.overflow = "hidden";
      return () => {
        window.removeEventListener("keydown", onKey);
        document.body.style.overflow = "";
      };
    }
  }, [openId, onClose]);

  if (!openId) return null;
  const url = videoMap[openId];
  const product = productById(openId);
  if (!url) return null;

  return (
    <AnimatePresence>
      {openId && (
        <motion.div
          className="fixed inset-0 z-[90] flex items-center justify-center p-4 md:p-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="video-modal"
        >
          <div className="absolute inset-0 bg-[#07090D]/95 backdrop-blur-md" onClick={onClose} />
          <motion.div
            initial={{ scale: 0.94, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.94, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-5xl"
          >
            <button
              onClick={onClose}
              className="absolute -top-12 right-0 w-10 h-10 rounded-full bg-[#1A1C22] border border-[#D8DCE2]/15 flex items-center justify-center hover:border-[#D7B56D]/40 transition-colors"
              data-testid="video-modal-close"
              aria-label={t("close")}
            >
              <X size={16} className="text-[#F5F1EA]" />
            </button>

            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#D7B56D] mb-2 hidden md:block">
              · {t("videoText")}
            </div>
            <h3 className="font-display text-xl md:text-2xl font-bold text-[#F5F1EA] mb-4 hidden md:block">
              {product?.name}
            </h3>

            <div className="relative w-full aspect-video rounded-2xl overflow-hidden border border-[#D7B56D]/30 bg-[#07090D]">
              <iframe
                className="absolute inset-0 w-full h-full"
                src={`${url}?autoplay=1&rel=0`}
                title={`${product?.name} demo`}
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                data-testid="video-iframe"
              />
            </div>

            <p className="text-[10px] text-[#D8DCE2]/40 mt-3 text-center">{t("disclaimer")}</p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
